import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Search, Plus, Copy, Upload, KeyRound, UserX, UserCheck } from "lucide-react";

export default function Students() {
    const [items, setItems] = useState([]);
    const [batches, setBatches] = useState([]);
    const [q, setQ] = useState("");
    const [batchFilter, setBatchFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [created, setCreated] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", mobile: "", batch_id: "" });
    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [resetResult, setResetResult] = useState(null);

    const load = async () => {
        const params = new URLSearchParams();
        if (q) params.append("q", q);
        if (batchFilter) params.append("batch_id", batchFilter);
        params.append("limit", "200");
        const { data } = await api.get(`/students?${params}`);
        setItems(data.items);
    };
    useEffect(() => { load(); }, [q, batchFilter]); // eslint-disable-line
    useEffect(() => { api.get("/batches").then(r => setBatches(r.data.items)); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/students", form);
            setCreated(data);
            setForm({ name: "", email: "", mobile: "", batch_id: "" });
            load();
            toast.success("Student created");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };
    const toggleStatus = async (u) => {
        const action = u.status === "active" ? "deactivate" : "activate";
        await api.post(`/students/${u.id}/${action}`);
        toast.success(`Student ${action}d`);
        load();
    };
    const resetPw = async (u) => {
        try {
            const { data } = await api.post(`/students/${u.id}/reset-password`);
            setResetResult({ name: u.name, temp_password: data.temp_password });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reset failed");
        }
    };

    const doImport = async () => {
        if (!importFile) return;
        const fd = new FormData();
        fd.append("file", importFile);
        try {
            const { data } = await api.post("/students/import", fd);
            setImportResult(data);
            load();
            toast.success(`Imported ${data.created_count} students`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Import failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="students-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Manage</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Students</h1>
                </div>
                <div className="flex gap-2">
                    <Dialog open={importOpen} onOpenChange={setImportOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline" className="rounded-xl" data-testid="csv-import-btn"><Upload className="h-4 w-4 mr-2" /> CSV import</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Bulk import students</DialogTitle>
                                <DialogDescription>CSV columns: <code>name,email,mobile,batch_id</code>. The system will auto-generate enrollment numbers and temporary passwords.</DialogDescription>
                            </DialogHeader>
                            <Input type="file" accept=".csv" onChange={(e) => setImportFile(e.target.files[0])} data-testid="csv-import-file" />
                            {importResult && (
                                <div className="mt-3 text-sm">
                                    <div className="font-semibold text-[#16A34A]">Created: {importResult.created_count}</div>
                                    {importResult.errors?.length > 0 && (
                                        <div className="text-[#E11D48] mt-2 max-h-32 overflow-auto text-xs">
                                            {importResult.errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
                                        </div>
                                    )}
                                </div>
                            )}
                            <DialogFooter>
                                <Button onClick={doImport} className="mx-btn-primary" data-testid="csv-import-submit">Import</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCreated(null); }}>
                        <DialogTrigger asChild>
                            <Button className="mx-btn-primary" data-testid="add-student-btn"><Plus className="h-4 w-4 mr-2" /> Add student</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>{created ? "Student created" : "Add new student"}</DialogTitle>
                                <DialogDescription>{created ? "Share these credentials with the student. They'll be asked to change the password on first login." : "Enrollment number and temporary password are auto-generated."}</DialogDescription>
                            </DialogHeader>
                            {created ? (
                                <div className="space-y-3" data-testid="created-student-info">
                                    <div className="bg-[#F0EFFF] dark:bg-[#1C1A2E] rounded-xl p-4">
                                        <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Email</div>
                                        <div className="font-semibold mt-1">{created.user.email}</div>
                                    </div>
                                    <div className="bg-amber-gradient text-white rounded-xl p-4">
                                        <div className="text-xs uppercase tracking-widest font-bold text-white/90">Temporary password</div>
                                        <div className="flex items-center justify-between mt-1">
                                            <code className="font-bold text-lg">{created.temp_password}</code>
                                            <Button size="sm" variant="ghost" onClick={() => copy(created.temp_password)} className="text-white hover:bg-white/20" data-testid="copy-temp-password">
                                                <Copy className="h-4 w-4 mr-1.5" /> Copy
                                            </Button>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Enrollment #</div>
                                        <div className="font-mono mt-1">{created.student_profile.enrollment_number}</div>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-4">
                                    <div><Label>Full name</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="student-name-input" /></div>
                                    <div><Label>Email</Label><Input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="student-email-input" /></div>
                                    <div><Label>Mobile</Label><Input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="student-mobile-input" /></div>
                                    <div>
                                        <Label>Batch</Label>
                                        <Select value={form.batch_id} onValueChange={(v) => setForm({ ...form, batch_id: v })}>
                                            <SelectTrigger data-testid="student-batch-select"><SelectValue placeholder="Select batch (optional)" /></SelectTrigger>
                                            <SelectContent>{batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <DialogFooter>
                                        <Button type="submit" className="mx-btn-primary" data-testid="student-create-submit">Create student</Button>
                                    </DialogFooter>
                                </form>
                            )}
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="pl-9 rounded-xl" data-testid="students-search" />
                </div>
                <Select value={batchFilter || "ALL"} onValueChange={(v) => setBatchFilter(v === "ALL" ? "" : v)}>
                    <SelectTrigger className="w-56" data-testid="students-batch-filter"><SelectValue placeholder="All batches" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">All batches</SelectItem>
                        {batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Badge variant="secondary">{items.length} students</Badge>
            </div>

            <Card className="mx-card">
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Student</TableHead>
                                <TableHead>Enrollment #</TableHead>
                                <TableHead>Batch</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="w-12"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map(u => {
                                const batch = batches.find(b => b.id === u.student_profile?.batch_id);
                                return (
                                    <TableRow key={u.id} data-testid={`student-row-${u.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="h-9 w-9 rounded-full bg-brand-gradient flex items-center justify-center text-white font-bold text-sm">{u.name?.[0]}</div>
                                                <div>
                                                    <div className="font-semibold text-sm">{u.name}</div>
                                                    <div className="text-xs text-muted-foreground">{u.email}</div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">{u.student_profile?.enrollment_number || "—"}</TableCell>
                                        <TableCell className="text-sm">{batch?.name || "Unassigned"}</TableCell>
                                        <TableCell>
                                            <Badge variant={u.status === "active" ? "default" : "secondary"} className={u.status === "active" ? "bg-[#16A34A] hover:bg-[#16A34A]" : ""}>
                                                {u.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" data-testid={`student-row-actions-${u.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => resetPw(u)}><KeyRound className="h-4 w-4 mr-2" /> Reset password</DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => toggleStatus(u)}>
                                                        {u.status === "active" ? <><UserX className="h-4 w-4 mr-2" /> Deactivate</> : <><UserCheck className="h-4 w-4 mr-2" /> Activate</>}
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-12">No students yet</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={!!resetResult} onOpenChange={(v) => { if (!v) setResetResult(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Password reset</DialogTitle>
                        <DialogDescription>
                            Share these temporary credentials with <strong>{resetResult?.name}</strong>. They'll be prompted to change the password on next login.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-muted rounded-xl px-4 py-3 flex items-center justify-between gap-3 font-mono text-base">
                        <span>{resetResult?.temp_password}</span>
                        <Button size="sm" variant="ghost" onClick={() => copy(resetResult?.temp_password)}>
                            <Copy className="h-4 w-4" />
                        </Button>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setResetResult(null)}>Done</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
